import {debounceTime, Options, TableDataRequest} from '@fl/cmsch-fe-library';
import {debounce, isEmpty, isNumber} from 'lodash/fp';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {none, Opt, opt} from 'ts-opt';

import {ColumnHeader} from 'api/gen/ColumnHeader';
import {UserProfileAssociationRole} from 'api/gen/UserProfileAssociationRole';
import {TFunction, useOurTranslation} from 'app/translations';
import {AsyncValidationSource} from 'utils/async-validation-source';
import {Errors} from 'utils/validator';

import {managementAssociationsRowMapper} from '../components/management-associations-table/row-mapper';
import {adminAction} from '../model';
import {
    managementAssociationsRoleOptionsSelector,
    managementAssociationsUserOptionsSelector,
    simpleAdminSelector,
} from '../model/selector';
import {CreateOrEditOrNull} from '../types/create-or-edit-or-null';
import {ManagementAssociationsTableType} from '../types/management-associations';
import {SubjectManagementFormValues} from '../types/subject-management-form-values';
import {defaultUserProfileAndRoleIds, UserProfileAndRole} from '../types/user-profile-and-role-ids';
import {managementAssociationsValidate} from '../utils/management-associations-validate';

const associationForm = 'associationForm';

interface UseManagementAssociationsSetup {
    associationId: Opt<number>;
    associationsOptions: Options<number>;
    managementAssociationsExtraColumns: Array<ColumnHeader>;
    roleOptions: Options<number>;
    userProfileOptions: Options<number>;
    userProfileAndRoles: UserProfileAndRole;
    visibleModal: boolean;
    modalTitle: string;
    associationDetail: Opt<UserProfileAssociationRole>;
    isEdit: boolean;
    t: TFunction<'admin'>;
    tCommon: TFunction<'common'>;
    searchManagementAssociations(text: string): void;
    handleManagementAssociationSelectChangeValue(value: Opt<number>): void;
    onManagementAssociationsTableFetch(tableDataRequest: Opt<TableDataRequest<ManagementAssociationsTableType>>): void;
    removeRolesForUser(_id: number, user: ManagementAssociationsTableType): void;
    removeUsersFromRole(columnRoleId: string): void;
    removeConfirmTitle(_id: number, user: ManagementAssociationsTableType): string;
    handleColumnOpenCreateModal(roleId: string, roleName: string): void;
    handleRowOpenCreateModal(_id: number, record: ManagementAssociationsTableType): void;
    handleOpenCreateModalWithoutLimitations(): void;
    handleSubmitModal(): void;
    handleUserSearch(searchText: string): void;
    handleRoleSearch(searchText: string): void;
    handleCloseModal(): void;
    validateDebounced(source: AsyncValidationSource): void;
    validate(values: SubjectManagementFormValues): Errors<SubjectManagementFormValues>;
    rowMapper(row: ManagementAssociationsTableType): ManagementAssociationsTableType;
    clearAssociationDetail(): void;
}

/** Builds the state and callbacks used by the association management screen. */
// eslint-disable-next-line max-lines-per-function
export const useManagementAssociationsSetup = (): UseManagementAssociationsSetup => {
    const [associationId, setAssociationId] = useState<Opt<number>>(none);
    const associationsOptions = useSelector(simpleAdminSelector.managementAssociationsOptions);
    const dispatch = useDispatch();
    const {t, tCommon} = useOurTranslation('admin');
    const [actionMode, setActionMode] = useState<CreateOrEditOrNull>(null);
    const [userProfileAndRoles, setUserProfileAndRoleIds] = useState<UserProfileAndRole>(defaultUserProfileAndRoleIds);
    const subjectManagementModalVisible = useSelector(simpleAdminSelector.subjectManagementModalVisible);
    const isEdit = useMemo(() => actionMode === 'edit', [actionMode]);
    const associationDetail = useSelector(simpleAdminSelector.managementAssociationDetail);

    useEffect(() => {
        if (!isEmpty(associationsOptions) && associationsOptions.length === 1) {
            const firstOption = opt(associationsOptions).headIn().prop('value');
            setAssociationId(opt(associationsOptions).headIn().prop('value'));
            dispatch(adminAction.getManagementAssociationsTableData(opt({action: 'reset'}), firstOption));
        }
    }, [associationsOptions, dispatch]);

    const visibleModal = useMemo(() =>
        subjectManagementModalVisible === associationForm
        && (!isEdit || associationDetail.isFull),
    [subjectManagementModalVisible, associationDetail, isEdit]);
    const roleOptions = useSelector(managementAssociationsRoleOptionsSelector);
    const userProfileOptions = useSelector(managementAssociationsUserOptionsSelector);
    const associationName = useMemo(() =>
        associationId.isFull ? associationsOptions.find(x => x.value === associationId.orUndef())?.label : '',
    [associationId, associationsOptions]);

    const modalTitle = useMemo(() => t(isEdit
        ? 'updateAccessToAssociation'
        : 'addAccessToAssociation', {association: associationName}),
    [t, associationName, isEdit]);

    const searchManagementAssociations = useCallback((text: string) => {
        dispatch(adminAction.getManagementAssociationsOptions(text));
    }, [dispatch]);

    const handleManagementAssociationSelectChangeValue = useCallback((value: Opt<number>) => {
        dispatch(adminAction.getManagementAssociationsTableData(opt({action: 'reset'}), value));
        setAssociationId(value);
    }, [dispatch]);

    const onManagementAssociationsTableFetch = useCallback((
        tableDataRequest: Opt<TableDataRequest<ManagementAssociationsTableType>>,
    ) => {
        if (tableDataRequest.isFull) {
            dispatch(adminAction.getManagementAssociationsTableData(tableDataRequest, associationId));
        }
    }, [associationId, dispatch]);

    const managementAssociationsExtraColumns = useSelector(simpleAdminSelector.managementAssociationsExtraColumns);

    const removeRolesForUser = useCallback((_id: number, user: ManagementAssociationsTableType) => {
        if (associationId.isFull) {
            dispatch(adminAction.removeAssociationRolesForUser(
                user.userProfileId,
                associationId.orCrash('Association id not found for removing user roles'),
            ));
        }
    }, [dispatch, associationId]);

    const clearAssociationDetail = useCallback(() => {
        dispatch(adminAction.getManagementAssociationDetailSuccess(none));
    }, [dispatch]);

    const removeUsersFromRole = useCallback((columnRoleId: string) => {
        const roleIdNarrowed = opt(columnRoleId).map(Number).narrow(isNumber);
        if (associationId.isFull && roleIdNarrowed.isFull) {
            dispatch(adminAction.removeUsersFromAssociationRole(
                roleIdNarrowed.orCrash('Role Id is not number'),
                associationId.orCrash('Association id not found for removing user roles'),
            ));
        }
    }, [dispatch, associationId]);

    const removeConfirmTitle = useCallback((_id: number, user: ManagementAssociationsTableType) =>
        t('confirmRemovingUserRoles', {user: user.username}), [t]);

    const handleSubmitModal = useCallback(() => {
        if (associationId.isFull) {
            const submitAction = isEdit
                ? adminAction.updateManagementAssociationDetail
                : adminAction.createManagementAssociation;
            const safeAssociationId = associationId.orCrash('Association id not found');
            dispatch(submitAction(safeAssociationId));
        }
    }, [isEdit, associationId, dispatch]);

    const handleOpenCreateModal = useCallback(() => {
        setActionMode('create');
        dispatch(adminAction.setManagementSubjectModalVisible(associationForm));
    }, [dispatch]);

    const handleOpenEditModal = useCallback(() => {
        setActionMode('edit');
        dispatch(adminAction.setManagementSubjectModalVisible(associationForm));
    }, [dispatch]);

    const handleDynamicCellClick = useCallback((userProfileAssociationRoleId: number) => {
        dispatch(adminAction.getManagementAssociationDetail(userProfileAssociationRoleId));
        handleOpenEditModal();
    }, [dispatch, handleOpenEditModal]);

    const rowMapper = useCallback((row: ManagementAssociationsTableType) =>
        managementAssociationsRowMapper(row, handleDynamicCellClick),
    [handleDynamicCellClick]);

    const handleColumnOpenCreateModal = useCallback((columnRoleId: string, roleName: string) => {
        const roleIdNarrowed = opt(columnRoleId).map(Number).narrow(isNumber);

        if (roleIdNarrowed.isFull) {
            setUserProfileAndRoleIds({
                role: {
                    id: roleIdNarrowed.orCrash('Role Id is not number'),
                    name: roleName,
                },
                userProfile: null,
            });
            handleOpenCreateModal();
        }
    }, [handleOpenCreateModal]);

    const handleRowOpenCreateModal = useCallback((_id: number, record: ManagementAssociationsTableType) => {
        setUserProfileAndRoleIds({
            role: null,
            userProfile: {
                id: record.userProfileId,
                name: record.username,
            },
        });
        handleOpenCreateModal();
    }, [handleOpenCreateModal]);

    const handleOpenCreateModalWithoutLimitations = useCallback(() => {
        setUserProfileAndRoleIds(defaultUserProfileAndRoleIds);
        handleOpenCreateModal();
    }, [handleOpenCreateModal]);

    const handleCloseModal = useCallback(() => {
        setActionMode(null);
        dispatch(adminAction.setManagementSubjectModalVisible(null));
        dispatch(adminAction.setManagementAssociationsUsers([]));
        dispatch(adminAction.setManagementAssociationsRoles([]));
    }, [dispatch]);

    const handleUserSearch = useCallback((searchText: string): void => {
        if (associationId.isFull) {
            dispatch(adminAction.searchManagementAssociationsUsers(
                searchText,
                associationId.orCrash('Unknown association id'),
            ));
        }
    }, [dispatch, associationId]);

    const validateData = useCallback((source: AsyncValidationSource) => {
        dispatch(adminAction.validateManagementAssociation(source));
    }, [dispatch]);

    const handleRoleSearch = useCallback((searchText: string): void => {
        dispatch(adminAction.searchManagementAssociationsRoles(searchText));
    }, [dispatch]);

    const validateDebounced = useMemo(() => debounce(debounceTime)(validateData), [validateData]);

    return {
        associationId,
        associationsOptions,
        managementAssociationsExtraColumns,
        roleOptions,
        userProfileAndRoles,
        userProfileOptions,
        visibleModal,
        modalTitle,
        associationDetail,
        isEdit,
        t,
        tCommon,
        searchManagementAssociations,
        handleManagementAssociationSelectChangeValue,
        onManagementAssociationsTableFetch,
        removeConfirmTitle,
        removeRolesForUser,
        removeUsersFromRole,
        handleCloseModal,
        handleColumnOpenCreateModal,
        handleOpenCreateModalWithoutLimitations,
        handleRoleSearch,
        handleRowOpenCreateModal,
        handleSubmitModal,
        handleUserSearch,
        validate: managementAssociationsValidate,
        rowMapper,
        clearAssociationDetail,
        validateDebounced,
    };
};
